import React, { useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { IconProvider } from '@/components/ui';
import { DialogField, DialogOption, DialogVariable, getCachedDialogs } from '@/lib/stores/dialogStore';
import { useSimplifiedUserData } from '@/providers/SimplifiedUserDataProvider';
import { useFieldEditingHandlers, FieldTextInput } from './BaseFieldEditor';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { DialogOptionEditor } from '../DialogOptionEditor';
import { ConnectionBadge } from './ConnectionBadge';

export interface QuestionFieldEditorProps {
  field: DialogField;
  fieldIndex: number;
  sectionIndex: number;
  sectionIdentifiers: string[];
  sections?: { section_identifier: string; name: string }[];
  currentSectionFields?: DialogField[];
  currentDialogId?: string;
  dialogTranslatable: boolean;
  speakerMode: 'none' | 'type' | 'free-text' | 'object';
  speakerTypeId?: string | null;
  speakerTemplateIds?: string[];
  dialogVariables?: DialogVariable[];
  onUpdateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  onRemoveField: (sectionIndex: number, fieldId: string) => void;
  onAddOption: (sectionIndex: number, fieldId: string) => void;
  onUpdateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  onRemoveOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  gameId?: string;
  t: any;
}

/**
 * QuestionFieldEditor handles 'question' field type
 * Includes text input and answer options list with drag-and-drop
 */
export const QuestionFieldEditor = React.memo(function QuestionFieldEditor({
  field,
  fieldIndex,
  sectionIndex,
  sectionIdentifiers,
  sections,
  currentSectionFields,
  currentDialogId,
  dialogTranslatable,
  speakerMode,
  speakerTypeId,
  speakerTemplateIds,
  dialogVariables,
  onUpdateField,
  onRemoveField,
  onAddOption,
  onUpdateOption,
  onRemoveOption,
  gameId,
  t,
}: QuestionFieldEditorProps) {
  const { selectedGame } = useSimplifiedUserData();
  const effectiveGameId = gameId || selectedGame?.id;

  const { locaKey, displayText, sourceText, isTranslationMode, handleTextChange, handleTextBlur } = useFieldEditingHandlers(
    field, effectiveGameId, onUpdateField, sectionIndex
  );

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.3 : 1,
  };

  // Sensors for option dragging
  const optionSensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );


  // Handle option drag end within this field
  const handleOptionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id || !field.options) return;

    const activeId = active.id as string;
    const overId = over.id as string;

    // Extract option indices from IDs
    const activeOptionIndex = parseInt(activeId.split('-')[3]);
    const overOptionIndex = parseInt(overId.split('-')[3]);

    if (activeOptionIndex !== -1 && overOptionIndex !== -1) {
      const reorderedOptions = arrayMove(field.options, activeOptionIndex, overOptionIndex);
      // Update order indices
      const updatedOptions = reorderedOptions.map((opt, i) => ({
        ...opt,
        order_index: i,
      }));
      onUpdateField(sectionIndex, field.id, { options: updatedOptions });
    }
  }, [field.options, sectionIndex, field.id, onUpdateField]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-start gap-2 p-2 border border-gray-200 rounded-sm bg-white"
    >
      {/* Drag Handle */}
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 pt-2"
        type="button"
      >
        <IconProvider iconstring="fa-grip-vertical" size={14} />
      </button>

      {/* Field Content */}
      <div className="flex-1 items-center justify-center space-y-2">
        {/* Field Text with Translation Icon */}
        <FieldTextInput
          field={field}
          displayText={displayText}
          locaKey={locaKey}
          sourceText={sourceText}
          isTranslationMode={isTranslationMode}
          dialogTranslatable={dialogTranslatable}
          onTextChange={handleTextChange}
          onTextBlur={handleTextBlur}
          onTypeChange={(type) => onUpdateField(sectionIndex, field.id, { type })}
          placeholder="Enter question text..."
          gameId={effectiveGameId}
        />

        {/* Options for Question Type */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm">Answer Choices</Label>

          </div>

          {field.options && field.options.length > 0 ? (
            <DndContext
              sensors={optionSensors}
              collisionDetection={closestCenter}
              onDragEnd={handleOptionDragEnd}
            >
              <SortableContext
                items={field.options.map((_, i) => `option-${sectionIndex}-${field.id}-${i}`)}
                strategy={verticalListSortingStrategy}
              >
                <div className="space-y-2 border-l-2 border-muted pl-3">
                  {field.options.map((option, optionIndex) => (
                    <DialogOptionEditor
                      key={`option-${sectionIndex}-${field.id}-${optionIndex}`}
                      option={option}
                      optionIndex={optionIndex}
                      sectionIndex={sectionIndex}
                      fieldId={field.id}
                      sectionIdentifiers={sectionIdentifiers}
                      sections={sections}
                      currentDialogId={currentDialogId}
                      dialogTranslatable={dialogTranslatable}
                      dialogVariables={dialogVariables}
                      onUpdateOption={(secIdx, fieldIdOrIndex, optIdx, updates) =>
                        onUpdateOption(secIdx, String(fieldIdOrIndex), optIdx, updates)
                      }
                      onRemoveOption={(secIdx, fieldIdOrIndex, optIdx) =>
                        onRemoveOption(secIdx, String(fieldIdOrIndex), optIdx)
                      }
                      gameId={effectiveGameId}
                      t={t}
                    />
                  ))}

                </div>

              </SortableContext>
             
            </DndContext>
          ) : (
            <p className="text-sm text-muted-foreground">
              No choices yet
            </p>
            
          )}
           <Button
                onClick={() => onAddOption(sectionIndex, field.id)}
                size="sm"
                variant="outline"
                type="button"
                className="w-full"
              >
                <IconProvider iconstring="fa-plus" className="mr-2" size={12} />
                Add Choice
              </Button>
        </div>
      </div>

      {/* Field Type & Buttons */}
      <div className="flex items-center gap-1">

        {/* Remove Field Button */}
        <Button
          variant="halfghost"
          size="sm"
          onClick={() => onRemoveField(sectionIndex, field.id)}
          className="px-2 text-xs w-8 h-8"
          type="button"
        >
          <IconProvider
            iconstring="fa-xmark"
            size={14}
            color="currentColor"
            className="hover:opacity-80 transition-colors"
          />
        </Button>
      </div>
    </div>
  );
});
